const API = (path: string) =>
  `${import.meta.env.VITE_API_BASE_URL || ""}${path}`;

async function get<T>(path: string): Promise<T> {
  const r = await fetch(API(path), { credentials: "include" });
  if (!r.ok) {
    if (r.status === 401 || r.status === 403)
      throw new Error("Brak uprawnień. Zaloguj się jako admin.");
    throw new Error(`HTTP ${r.status}`);
  }
  return r.json() as Promise<T>;
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const r = await fetch(API(path), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json() as Promise<T>;
}

async function del<T>(path: string): Promise<T> {
  const r = await fetch(API(path), {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json() as Promise<T>;
}

export type StatusCount = { status: number; count: number; sum: number };
export type DashboardData = {
  last30Days: StatusCount[];
  lastSale: { id: string; status: number; amount: number; currency: string; createdAt: string; email: string } | null;
  users: { total: number; admins: number };
  kickEventsLast24h: number;
};

export type SaleListItem = {
  id: string;
  status: number;
  amount: number;
  currency: string;
  email: string;
  customerName: string | null;
  messagePreview: string;
  createdAt: string;
  paidAt: string | null;
  answeredAt: string | null;
  hasResponse: boolean;
  hasInvoice: boolean;
};
export type SalesPage = { total: number; items: SaleListItem[] };

export type SaleDetail = {
  id: string;
  status: number;
  amount: number;
  currency: string;
  email: string;
  customerName: string | null;
  message: string;
  responseText: string | null;
  failureReason: string | null;
  providerKey: string;
  providerSessionId: string | null;
  providerPaymentId: string | null;
  createdAt: string;
  paidAt: string | null;
  answeredAt: string | null;
  failedAt: string | null;
  invoiceId: string | null;
  invoiceIssuedAt: string | null;
  outbound: OutboundCallRow[];
};
export type OutboundCallRow = {
  id: number;
  target: string;
  method: string;
  url: string;
  statusCode: number | null;
  durationMs: number;
  timestamp: string;
  errorMessage: string | null;
};

export type AdminUser = {
  id: string;
  kickUserId: string;
  kickUsername: string;
  email: string | null;
  role: "Admin" | "Visitor";
  createdAt: string;
  lastLoginAt: string;
  loginCount: number;
};

export const SALE_STATUS_LABEL: Record<number, string> = {
  0: "Created",
  1: "Pending",
  2: "Paid",
  3: "Answered",
  4: "Invoiced",
  9: "Failed",
  10: "Cancelled",
};

export const adminApi = {
  dashboard: () => get<DashboardData>("/api/admin/dashboard"),
  sales: (params: { status?: string; email?: string; skip?: number; take?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.email) q.set("email", params.email);
    if (params.skip != null) q.set("skip", String(params.skip));
    if (params.take != null) q.set("take", String(params.take));
    const qs = q.toString();
    return get<SalesPage>(`/api/admin/sales${qs ? "?" + qs : ""}`);
  },
  saleDetail: (id: string) => get<SaleDetail>(`/api/admin/sales/${id}`),
  users: () => get<AdminUser[]>("/api/admin/users"),
  setUserRole: (id: string, role: "Admin" | "Visitor") =>
    post<{ id: string; role: string }>(`/api/admin/users/${id}/role`, { role }),
  recentBroadcasts: (take = 50) =>
    get<Array<{ id: number; timestamp: string; statusCode: number | null; durationMs: number; errorMessage: string | null; requestBody: string | null; responseBody: string | null }>>(`/api/admin/broadcasts/recent?take=${take}`),
  replaySale: (id: string) =>
    post<{ success: boolean; connectedDevices: number; durationMs: number; error: string | null }>(`/api/admin/sales/${id}/replay`),
  issueInvoice: (id: string) =>
    post<{ invoiceId: string }>(`/api/sales/${id}/invoice`),

  // Queue
  getQueue: () => get<QueueSnapshot>(`/api/admin/queue`),
  pauseQueue: () => post<{ paused: boolean }>(`/api/admin/queue/pause`),
  resumeQueue: () => post<{ paused: boolean }>(`/api/admin/queue/resume`),
  clearQueue: () => post<{ skipped: number }>(`/api/admin/queue/clear`),
  removeJob: async (id: string) => {
    const r = await fetch(API(`/api/admin/queue/${id}`), { method: "DELETE", credentials: "include" });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json() as Promise<{ removed: string }>;
  },

  // Settings
  getSettings: () => get<BroadcastSettings>(`/api/admin/settings`),
  updateSettings: (patch: Partial<BroadcastSettings>) =>
    post<BroadcastSettings>(`/api/admin/settings`, patch),

  // Feature toggles
  getToggles: () => get<FeatureToggles>(`/api/admin/feature-toggles`),
  updateToggles: (patch: Partial<FeatureToggles>) =>
    post<FeatureToggles>(`/api/admin/feature-toggles`, patch),

  // Diagnostics — integration self-tests
  testDonation: (body: TestDonationPayload) =>
    post<TestDonationResult>(`/api/admin/test-donation`, body),
  testInvoice: (body: TestInvoicePayload) =>
    post<TestInvoiceResult>(`/api/admin/subiekt/test-invoice`, body),
  kickStatus: () => get<KickStatus>(`/api/admin/kick/status`),
  // Kick recovery — every one is destructive, the UI confirms first.
  kickDeleteSub: (id: string) =>
    del<{ deleted: string; usingToken: string }>(
      `/api/admin/kick/subs/${encodeURIComponent(id)}`
    ),
  kickUnsubscribeAll: () =>
    post<{ usingToken: string; total: number; deleted: number; failed: string[] }>(
      `/api/admin/kick/subs/unsubscribe-all`,
      {}
    ),
  kickRevokeToken: () =>
    post<{ revoked: string; kickUserId: string }>(`/api/admin/kick/token/revoke`, {}),
  kickDeleteToken: () =>
    del<{ deleted: string; kickUserId: string }>(`/api/admin/kick/token`),
  subiektStatus: () => get<SubiektStatus>(`/api/admin/subiekt/status`),

  // Graph queries (SQL Server NODE/EDGE)
  graphPresets: () => get<GraphPresetDescriptor[]>(`/api/admin/graph/presets`),
  graphRunPreset: (key: string, args: Record<string, string>) => {
    const qs = new URLSearchParams(args).toString();
    return get<GraphResult>(`/api/admin/graph/preset/${encodeURIComponent(key)}${qs ? "?" + qs : ""}`);
  },
  graphRunAdHoc: (sql: string, limit: number) =>
    post<GraphResult>(`/api/admin/graph/query`, { sql, limit }),
  graphLlmStatus: () => get<OllamaStatus>(`/api/admin/graph/llm-status`),
  graphLlmPull: () => post<{ ok: boolean; message: string }>(`/api/admin/graph/llm-pull`, {}),
  graphExplain: (viewerId: string, question: string) =>
    post<ExplainResult>(`/api/admin/graph/explain`, { viewerId, question }),

  // Viewer search (typeahead for ViewerPicker)
  viewerSearch: (q: string, take = 20) => {
    const qs = new URLSearchParams({ q, take: String(take) }).toString();
    return get<ViewerSearchHit[]>(`/api/admin/viewers/search?${qs}`);
  },
};

export type ViewerSearchHit = {
  viewerId: string;
  kickUserId: string;
  kickUsername: string;
  isSubscriber: boolean;
  totalDonatedPln: number;
  chatMessageCount: number;
  rankScore: number;
  lastSeenAt: string;
};

export type OllamaStatus = {
  reachable: boolean;
  modelPresent: boolean;
  model: string;
  error: string | null;
};

export type ExplainResult = {
  answer: string;
  messagesAnalysed: number;
  model: string;
};

export type GraphPresetDescriptor = {
  key: string;
  label: string;
  description: string;
  args: string[];
};

export type GraphResult = {
  columns: string[];
  rows: (string | number | boolean | null)[][];
  rowCount: number;
};

export type TestDonationPayload = {
  donorName?: string;
  amount?: number;
  currency?: string;
  message?: string;
  donorEmail?: string | null;
  systemPromptOverride?: string;
  maxTokens?: number;
};
export type TestDonationResult = {
  success: boolean;
  phase1Text: string | null;
  responseText: string | null;
  connectedDevices: number;
  durationMs: number;
  error: string | null;
};

export type TestInvoicePayload = {
  email: string;
  customerName?: string;
  amount: number;
  currency?: string;
  message?: string;
};
export type TestInvoiceResult = {
  success: boolean;
  invoiceId: string | null;
  pdfPath: string | null;
  error: string | null;
  fakeSaleId: string;
};

export type KickStatus = {
  config: {
    webhookUrl: string;
    redirectUri: string;
    channelSlug: string;
    scopes: string;
    clientIdConfigured: boolean;
  };
  token: { present: false } | {
    present: true;
    kickUsername: string;
    kickUserId: string;
    scope: string;
    issuedAt: string;
    expiresAt: string;
    refreshedAt: string | null;
    revoked: boolean;
    expired: boolean;
  };
  subscriptions: {
    count: number;
    items: Array<{ id: string; event: string; version: string; method: string; createdAt: string }>;
    error: string | null;
  };
  webhookDeliveries: { total: number; lastReceivedAt: string | null };
  hint: string;
};

export type SubiektStatus = {
  config: {
    clientIdConfigured: boolean;
    subscriptionKeyConfigured: boolean;
    redirectUri: string;
    apiBaseUrl: string;
    productName: string;
    autoIssueAfterAnswered: boolean;
  };
  featureToggles: { invoiceIssuingEnabled: boolean };
  token: { present: false } | {
    present: true;
    issuedAt: string;
    expiresAt: string;
    refreshedAt: string | null;
    revoked: boolean;
    hasRefreshToken: boolean;
    expired: boolean;
  };
  invoicesIssued: {
    count: number;
    last: { id: string; invoiceId: string; invoiceIssuedAt: string; email: string } | null;
  };
  hint: string;
};

export type FeatureToggles = {
  invoiceIssuingEnabled: boolean;
  voiceResponseEnabled: boolean;
  viewerAnalysisEnabled: boolean;
  chatReplyEnabled: boolean;
};

export type QueueJob = {
  id: string;
  kind: number;        // 0=Live, 1=Replay
  label: string;
  donation: { donorName: string; amount: number; currency: string; message: string | null };
  systemPrompt?: string;
  presetResponseText?: string | null;
  maxTokens?: number;
  queuedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  status: string;       // queued | running | done | failed | skipped
  error: string | null;
  steps: string[];      // diagnostic timeline appended by BroadcastService
};

export type QueueSnapshot = {
  paused: boolean;
  current: QueueJob | null;
  pending: QueueJob[];
  recentlyDone: QueueJob[];
};

export type BroadcastSettings = {
  phase1Destination: number;  // 0=Both, 1=Carp, 2=Overlay
  donorVoiceId: string;
  queuePaused: boolean;
};

export const DESTINATION_LABEL: Record<number, string> = {
  0: "Both — Karp + Overlay",
  1: "Carp — tylko fizyczna ryba",
  2: "Overlay — tylko warstwa OBS",
};
export const JOB_KIND_LABEL: Record<number, string> = { 0: "Live donejt", 1: "Replay" };
